'use client';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Button } from '@pickleballinc/react-ui';
import { Credenza, CredenzaContent } from '@pickleballinc/ui/components/ui/credenza';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import ImageWithFallback from '@/components/ImageWithFallback/ImageWithFallback';
import { PickleballCentralLogo } from '@/modules/players/v3/components/images/pickleball-central-logo';
import { Gear } from '@/modules/players/v3/types';
import { safeSanitizeHtml } from '@/modules/players/v3/utils/utils';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

interface ProductDetailsModalPrimaryAction {
  label: string;
  icon?: IconDefinition;
  variant?: 'primary' | 'secondary';
  onClick: () => void;
  disabled?: boolean;
}

interface ProductDetailsModalProps {
  gear: Gear;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isWebview?: boolean;
  primaryAction?: ProductDetailsModalPrimaryAction;
}

export const ProductDetailsModal = ({
  gear,
  open,
  onOpenChange,
  isWebview,
  primaryAction,
}: ProductDetailsModalProps) => {
  const sortedImages = useMemo(
    () => [...gear.images].sort((a, b) => a.sortOrder - b.sortOrder),
    [gear.images]
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const fallbackSrc = getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 450, 450);
  const total = sortedImages.length;
  const current = sortedImages[currentIndex];
  const description = safeSanitizeHtml(gear.description);

  const goTo = (index: number) => setCurrentIndex(Math.max(0, Math.min(total - 1, index)));

  return (
    <Credenza open={open} onOpenChange={onOpenChange}>
      <CredenzaContent
        className='md:!max-w-[600px] grid-cols-1 !p-0 !gap-0 overflow-hidden'
        dialogTitle={gear.name}
      >
        <div className='flex h-[720px] max-h-[90vh] flex-col bg-white'>
          <div className='flex shrink-0 items-center px-6 py-3.5'>
            <PickleballCentralLogo clickable={false} width={150} height={44} />
          </div>

          <div className='shrink-0 px-6'>
            <div className='relative flex h-[180px] md:h-[260px] w-full items-center justify-center rounded-xl border border-gray-200 p-2'>
              <ImageWithFallback
                src={current?.urlStandard ?? current?.urlThumbnail}
                fallbackSrc={fallbackSrc}
                alt={gear.name}
                className='size-full object-contain'
              />
              {total > 1 ? (
                <span className='absolute right-2 top-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm font-medium text-gray-700'>
                  {`${currentIndex + 1}/${total}`}
                </span>
              ) : null}
            </div>
          </div>

          {total > 1 ? (
            <div className='flex shrink-0 items-center justify-between gap-2 px-6 py-3'>
              <button
                type='button'
                aria-label='Previous image'
                disabled={currentIndex === 0}
                onClick={() => goTo(currentIndex - 1)}
                className='flex size-11 shrink-0 items-center justify-center rounded-md text-gray-500 disabled:opacity-40'
              >
                <FontAwesomeIcon icon={faChevronLeft} className='size-5' />
              </button>
              <div className='min-w-0 flex-1 overflow-x-auto'>
                <div className='mx-auto flex w-fit items-center gap-2'>
                  {sortedImages.map((image, i) => (
                    <button
                      key={image.id}
                      type='button'
                      aria-label={`View image ${i + 1}`}
                      onClick={() => goTo(i)}
                      className={clsx(
                        'size-[52px] md:size-[66px] shrink-0 overflow-hidden rounded-lg border',
                        i === currentIndex ? 'border-purple-700' : 'border-gray-200'
                      )}
                    >
                      <ImageWithFallback
                        src={image.urlThumbnail}
                        fallbackSrc={fallbackSrc}
                        alt=''
                        className='size-full object-cover'
                      />
                    </button>
                  ))}
                </div>
              </div>
              <button
                type='button'
                aria-label='Next image'
                disabled={currentIndex === total - 1}
                onClick={() => goTo(currentIndex + 1)}
                className='flex size-11 shrink-0 items-center justify-center rounded-md text-gray-500 disabled:opacity-40'
              >
                <FontAwesomeIcon icon={faChevronRight} className='size-5' />
              </button>
            </div>
          ) : null}

          <div className='flex min-h-0 flex-1 flex-col gap-2 px-6 py-4'>
            <p className='shrink-0 text-xl font-bold text-gray-900'>{gear.name}</p>
            {description ? (
              <p className='min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap text-sm text-gray-600'>
                {description}
              </p>
            ) : null}
          </div>

          {primaryAction || !isWebview ? (
            <div className='flex shrink-0 flex-row items-center gap-3 px-6 pb-6 pt-2'>
              {!isWebview ? (
                <a
                  href={gear.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={`${gear.name} on Pickleball Central (opens in a new tab)`}
                  className='flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#f16622] px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f16622] focus-visible:ring-offset-2 no-underline'
                >
                  See on Pickleball Central
                </a>
              ) : null}
              {primaryAction ? (
                <Button
                  variant={primaryAction.variant ?? 'primary'}
                  size='lg'
                  className='flex-1 justify-center gap-2'
                  disabled={primaryAction.disabled}
                  onClick={primaryAction.onClick}
                >
                  {primaryAction.icon ? <FontAwesomeIcon icon={primaryAction.icon} /> : null}
                  {primaryAction.label}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </CredenzaContent>
    </Credenza>
  );
};
